import React, { useState } from "react";
import { useGov } from "@/hooks/governance";
import { adaFromLovelace } from "@/lib/governance";
import { formatAdaBigCount, unixToDate } from "@/lib/format";
import { Badge, HashCell } from "@/components/primitives";
import { Table } from "@/components/Table";
import {
  GovPageShell, GovCard, GovRow, GovError, GovSkeleton, GovEmpty,
  GovTabs, GovPager, GovTallyBar, useGovNav,
} from "@/components/governance/GovShell";
import { entityVoteColumns } from "@/components/governance/GovParts";
import { DrepShare } from "@/components/governance/DrepShare";

const PAGE = 25;

const Feed = ({ path, columns, empty, pageSize = PAGE, order }) => {
  const [page, setPage] = useState(1);
  const { data, error, loading, refetch } = useGov(path, { params: { count: pageSize, page, ...(order ? { order } : {}) } });
  const rows = Array.isArray(data) ? data : [];
  if (error) return <GovError error={error} onRetry={refetch} />;
  return (
    <>
      <Table columns={columns} rows={rows} loading={loading} skeletonRows={8} empty={empty} />
      <GovPager page={page} onPage={setPage} hasNext={rows.length === pageSize} loading={loading} />
    </>
  );
};

const PowerFeed = ({ drepId }) => {
  const { data, error, loading, refetch } = useGov(`/governance/dreps/${drepId}/voting_power`);
  if (error) return <GovError error={error} onRetry={refetch} />;
  if (loading && !data) return <GovSkeleton lines={5} />;
  const epochs = Array.isArray(data?.epochs) ? [...data.epochs].reverse() : [];
  return (
    <Table
      columns={[
        { key: "epoch", label: "Epoch", render: (r) => <span className="text-foreground">{r.epoch}</span> },
        { key: "power", label: "Voting Power", align: "right", render: (r) => (r.data_status === "available" ? <span className="text-foreground">{formatAdaBigCount(r.voting_power ?? r.amount)} ₳</span> : <span className="text-amber-300/80">{r.unavailable_reason || "unavailable"}</span>) },
        { key: "in", label: "In Distribution", align: "right", render: (r) => (r.in_distribution ? <Badge tone="success">yes</Badge> : <Badge>no</Badge>) },
      ]}
      rows={epochs}
      empty="No voting-power history."
    />
  );
};

export const DrepDetail = ({ meta, drepId }) => {
  const navigate = useGovNav();
  const { data: d, error, loading, refetch } = useGov(`/governance/dreps/${drepId}`);
  const [tab, setTab] = useState("profile");

  const tabs = [
    { key: "profile", label: "Profile" },
    { key: "votes", label: "Votes" },
    { key: "delegators", label: "Delegators" },
    { key: "power", label: "Voting Power" },
  ];

  return (
    <GovPageShell crumbs={meta.crumbs} title={meta.title} subtitle={drepId} actions={d ? <DrepShare drepId={drepId} d={d} /> : null}>
      {error ? (
        <GovError error={error} onRetry={refetch} />
      ) : loading && !d ? (
        <GovSkeleton lines={8} />
      ) : d ? (
        <>
          <GovTabs tabs={tabs} active={tab} onChange={setTab} />
          <div data-testid={`gov-drep-tab-${tab}`}>
            {tab === "profile" && (
              <div className="grid lg:grid-cols-2 gap-4">
                <GovCard>
                  <GovRow label="DRep ID"><HashCell value={d.drep_id} len={14} /></GovRow>
                  <GovRow label="CIP-105"><HashCell value={d.drep_id_cip105} len={12} /></GovRow>
                  <GovRow label="Status"><Badge tone={d.special ? "info" : d.retired ? "default" : "success"}>{d.special ? "special" : d.status}</Badge></GovRow>
                  <GovRow label="Voting Power">{adaFromLovelace(d.voting_power)}</GovRow>
                  <GovRow label="Power Epoch">{d.voting_power_epoch ?? "—"}</GovRow>
                  <GovRow label="Delegators">{d.delegators_count ?? "—"}</GovRow>
                  <GovRow label="Deposit">{d.deposit ? adaFromLovelace(d.deposit) : "—"}</GovRow>
                  <GovRow label="Active Until">{d.active_until ? `epoch ${d.active_until}` : "—"}</GovRow>
                  {d.registration ? <GovRow label="Registered">epoch {d.registration.epoch} · {unixToDate(d.registration.block_time)}</GovRow> : null}
                </GovCard>
                <GovCard>
                  <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mono mb-3">Voting Record</div>
                  {d.votes ? (
                    <>
                      <GovTallyBar yes={d.votes.yes} no={d.votes.no} abstain={d.votes.abstain} />
                      <div className="flex items-center justify-between text-[12px] mono mt-3">
                        <span className="text-emerald-400">{d.votes.yes} yes</span>
                        <span className="text-red-400">{d.votes.no} no</span>
                        <span className="text-muted-foreground">{d.votes.abstain} abstain</span>
                        <span className="text-foreground">{d.votes.total} total</span>
                      </div>
                    </>
                  ) : <GovEmpty>No votes.</GovEmpty>}
                </GovCard>
              </div>
            )}
            {tab === "votes" && <Feed path={`/governance/dreps/${drepId}/votes`} columns={entityVoteColumns(navigate)} order="desc" empty="This DRep has not voted yet." />}
            {tab === "delegators" && (
              <Feed
                path={`/governance/dreps/${drepId}/delegators`}
                columns={[
                  { key: "addr", label: "Stake Address", render: (r) => <HashCell value={r.stake_address} len={14} /> },
                  { key: "epoch", label: "Since Epoch", align: "right", render: (r) => <span className="text-muted-foreground">{r.epoch ?? "—"}</span> },
                  { key: "time", label: "Delegated", align: "right", render: (r) => <span className="text-muted-foreground">{r.block_time ? unixToDate(r.block_time) : "—"}</span> },
                  { key: "tx", label: "Tx", align: "right", render: (r) => <HashCell value={r.tx_hash} len={8} /> },
                ]}
                empty="No delegators."
              />
            )}
            {tab === "power" && <PowerFeed drepId={drepId} />}
          </div>
        </>
      ) : <GovEmpty />}
    </GovPageShell>
  );
};
